import { useEffect, useState } from "react"

import { useFetchHomeGroups } from "src/data/homeGroups/queries/homeGroupQueries"
import {
  THomeGroup,
  THomeGroupFilter,
  THomeGroupType,
  TMaybeHomeGroup,
} from "src/data/homeGroups/types/homeGroupTypes"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useTranslate } from "src/i18n/useTranslate"
import { ComboboxStateful } from "src/ui/Combobox/ComboboxStateful"
import { purgeEmpty } from "src/utils/genericUtil"

type TSelectedHomeGroup = {
  label: string
  value: string
}

export type THomeGroupDropdownCommonProps = {
  required?: boolean
  type?: THomeGroupType
  baseFilter?: Omit<THomeGroupFilter, "type">
  error?: string
}

type THomeGroupsDropdownMultipleProps = {
  value: THomeGroup[]
  initialValue?: string[]
  onSelect: (homegroups: THomeGroup[]) => void
  multiple: true
}

type THomeGroupsDropdownSingleProps = {
  value: TMaybeHomeGroup
  multiple?: false
  initialValue?: string
  onSelect: (h: TMaybeHomeGroup) => void
}

type THomeGroupDropdownProps = THomeGroupDropdownCommonProps &
  (THomeGroupsDropdownMultipleProps | THomeGroupsDropdownSingleProps)

export function HomeGroupsDropdown({
  value,
  onSelect,
  required,
  initialValue,
  type = "area",
  baseFilter,
  multiple,
  error,
}: THomeGroupDropdownProps) {
  const { t, langKeys } = useTranslate()
  const [search, setSearch] = useState("")
  const { orgId } = useOrganization()
  const fetchHomeGroups = useFetchHomeGroups({
    orgId,
    filter: purgeEmpty({ ...baseFilter, name: search || undefined, type }),
  })

  const options = (fetchHomeGroups.data?.homegroups ?? []).map((homegroup) => ({
    label: homegroup.name,
    value: homegroup.id,
  }))

  function onChange(val: TSelectedHomeGroup[]) {
    if (multiple) {
      const selectedIds = val.map((homeGroup) => homeGroup.value)

      const filteredValue = value.filter((homeGroup) =>
        selectedIds.includes(homeGroup.id)
      )

      const newlyAddedHomeGroups =
        fetchHomeGroups.data?.homegroups.filter((homeGroup) =>
          selectedIds?.some((value) => value === homeGroup.id)
        ) ?? []

      const homeGroups = [...filteredValue, ...newlyAddedHomeGroups]

      const unique = homeGroups.filter(
        (val, index, array) =>
          array.map(({ id }) => id).indexOf(val.id) === index
      )

      onSelect(unique)
    }
  }

  // If an initial id is provided, set that as our initial value as soon as the
  // data has been loaded.
  useEffect(() => {
    if (multiple) {
      if (
        initialValue &&
        initialValue.length > 0 &&
        value.length === 0 &&
        fetchHomeGroups.data?.homegroups
      ) {
        const initialHomegroups =
          fetchHomeGroups.data?.homegroups.filter((homeGroup) =>
            initialValue.some((value) => value === homeGroup.id)
          ) ?? []

        if (initialHomegroups.length > 0) {
          onSelect(initialHomegroups)
        }
      }
    } else {
      if (initialValue && !value?.name && fetchHomeGroups.data?.homegroups) {
        const initialHomegroup = fetchHomeGroups.data?.homegroups.find(
          (hg) => hg.id === initialValue
        )

        if (initialHomegroup) {
          onSelect(initialHomegroup)
        }
      }
    }
  }, [
    fetchHomeGroups.data?.homegroups,
    initialValue,
    multiple,
    onSelect,
    value,
  ])

  if (multiple) {
    return (
      <ComboboxStateful
        error={error}
        multiple
        options={options}
        selectedValue={
          value?.map((homeGroup) => ({
            label: homeGroup.name,
            value: homeGroup.id,
          })) ?? []
        }
        onSearch={setSearch}
        onChange={onChange}
        label={
          type === "area"
            ? t(langKeys.organizations_home_group)
            : t(langKeys.building)
        }
        required={required}
      />
    )
  }

  return (
    <ComboboxStateful
      error={error}
      options={options}
      selectedValue={value?.id || ""}
      onSearch={setSearch}
      onChange={(value) => {
        const selectedHomeGroup = fetchHomeGroups.data?.homegroups.find(
          (homegroup) => homegroup.id === value
        )

        if (selectedHomeGroup) {
          onSelect(selectedHomeGroup)
        }
      }}
      label={
        type === "area"
          ? t(langKeys.organizations_home_group)
          : t(langKeys.building)
      }
      required={required}
    />
  )
}
